import { Part, ModuleSpec } from './types';
import { inferRole } from './inferRole';

const A = (30 * Math.PI) / 180;
const cosA = Math.cos(A), sinA = Math.sin(A);

function drawBox(x: number, y: number, z: number, w: number, d: number, h: number, scale: number, makePt: (x:number, y:number, z:number) => string, role: string) {
  let fTop = "url(#gTop)", fFront = "url(#gFront)", fRight = "url(#gSide)", stroke = "#334155", opacity = 1.0;
  
  if (role === 'back') {
    fTop = "url(#gBack)"; fFront = "url(#gBack)"; fRight = "url(#gBack)";
    stroke = "#64748b"; opacity = 0.85;
  } else if (role === 'shelf') {
    fTop = "url(#gShelf)"; fFront = "#c8d6e5"; fRight = "#c8d6e5";
    stroke = "#475569"; opacity = 0.85;
  } else if (role === 'door') {
    fTop = "url(#gAcrylic)"; fFront = "url(#gAcrylic)"; fRight = "url(#gAcrylic)";
    stroke = "#38bdf8"; opacity = 0.8;
  } else if (role === 'led') {
    fTop = "url(#gLedBar)"; fFront = "#d97706"; fRight = "#d97706";
    stroke = "#b45309"; opacity = 0.95;
  } else if (role === 'sideL' || role === 'sideR' || role === 'bottom' || role === 'top') {
    // Basic body panels
    fTop = "url(#gTop)"; fFront = "url(#gFront)"; fRight = "url(#gSide)";
    stroke = "#334155"; opacity = 0.95;
  }

  let html = '';
  // Top
  html += `<polygon points="${makePt(x, y, z+h)} ${makePt(x+w, y, z+h)} ${makePt(x+w, y+d, z+h)} ${makePt(x, y+d, z+h)}" fill="${fTop}" stroke="${stroke}" stroke-width="0.8" opacity="${opacity}"/>`;
  // Front
  html += `<polygon points="${makePt(x, y, z)} ${makePt(x+w, y, z)} ${makePt(x+w, y, z+h)} ${makePt(x, y, z+h)}" fill="${fFront}" stroke="${stroke}" stroke-width="0.8" opacity="${opacity}"/>`;
  // Right
  html += `<polygon points="${makePt(x+w, y, z)} ${makePt(x+w, y+d, z)} ${makePt(x+w, y+d, z+h)} ${makePt(x+w, y, z+h)}" fill="${fRight}" stroke="${stroke}" stroke-width="0.8" opacity="${opacity}"/>`;
  
  return html;
}

export function renderIsometricSvg(spec: ModuleSpec): string {
  const W = spec.W;
  const D = spec.D;
  const H = spec.H;
  const T = 18;

  // 1. 추론 + 파츠 전개
  const positionedParts: Part[] = [];
  
  let sideT = 18;
  let backT = 3;
  let shelfCount = 0;
  
  for (const raw of spec.parts) {
    const role = inferRole(raw, spec);
    if (role === 'sideL') sideT = raw.w || sideT;
    if (role === 'back') backT = raw.h <= 30 ? raw.h : raw.d <= 30 ? raw.d : 3;
    if (role === 'shelf') shelfCount += (raw.qty || 1);
  }
  
  let currentShelfIdx = 1;
  const shelfGap = (H - sideT * 2) / (shelfCount + 1);

  for (const raw of spec.parts) {
    const role = inferRole(raw, spec);
    const qty = raw.qty || 1;
    
    for (let i = 0; i < qty; i++) {
      let p = { ...raw, role };
      
      // 파츠가 매우 얇은 뒷판인데 위치 조정
      if (p.role === 'back') {
         if (p.d > 30) { p.h = p.d; p.d = backT; } // 스왑
      }

      if (role === 'bottom') {
        p.posX = 0; p.posY = 0; p.posZ = 0;
      } else if (role === 'top') {
        p.posX = 0; p.posY = 0; p.posZ = H - p.h;
      } else if (role === 'sideL') {
        if (i === 1) p.role = 'sideR';
        p.posX = p.role === 'sideL' ? 0 : W - p.w;
        p.posY = 0; 
        p.posZ = p.h === H ? 0 : sideT; 
      } else if (role === 'back') {
        p.posX = (W - p.w) / 2;
        p.posY = D - p.d; 
        p.posZ = (H - p.h) / 2;
      } else if (role === 'shelf') {
        p.posX = sideT;
        p.posY = 0;
        p.posZ = sideT + shelfGap * currentShelfIdx;
        currentShelfIdx++;
      } else if (role === 'door') {
        p.posX = sideT + (i * p.w);
        p.posY = 0; 
        p.posZ = sideT;
      } else if (role === 'led') {
        p.posX = sideT;
        p.posY = D * 0.2;
        p.posZ = H - sideT - 15;
        p.w = p.w || (W - sideT*2);
        p.d = 10;
        p.h = 5;
      } else {
        p.posX = 0; p.posY = 0; p.posZ = 0;
      }
      
      if (raw.posX !== undefined) p.posX = raw.posX;
      if (raw.posY !== undefined) p.posY = raw.posY;
      if (raw.posZ !== undefined) p.posZ = raw.posZ;
      
      positionedParts.push(p);
    }
  }

  // 2. z-index 정렬
  const roleOrder: Record<string, number> = {
    back: 1, sideL: 2, bottom: 3, top: 4, shelf: 5, sideR: 6, door: 7, led: 8, other: 9
  };
  positionedParts.sort((a, b) => roleOrder[a.role] - roleOrder[b.role]);

  // 3. 뷰포트
  const bayCount = spec.bayCount || 1;
  const drawCount = Math.min(bayCount, 3);
  const maxDim = Math.max(W, H);
  const scale = 110 / maxDim;
  const wScale = W * scale;
  const dScale = D * scale;
  const hScale = H * scale;
  
  const unitGap = 10;
  const svgW = Math.ceil(wScale * 0.866 * drawCount + dScale * 0.866 + unitGap * drawCount + 80);
  const svgH = Math.ceil(hScale + wScale * 0.5 * drawCount + dScale * 0.5 + unitGap * drawCount + 60);

  const makeTransform = (ox: number, oy: number) => {
    return (x: number, y: number, z: number) =>
      `${(ox - x * 0.866 + y * 0.866).toFixed(1)},${(oy + x * 0.5 + y * 0.5 - z).toFixed(1)}`;
  };
  
  const makeTransformA = (ox: number, oy: number) => {
    return (x: number, y: number, z: number): [number, number] =>
      [ox - x * 0.866 + y * 0.866, oy + x * 0.5 + y * 0.5 - z];
  };

  let svg = `<div style="margin:8px auto;text-align:center;overflow:visible;">`;
  svg += `<svg width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}" `
       + `style="filter:drop-shadow(0 4px 8px rgba(0,0,0,0.18));font-family:Pretendard,sans-serif;overflow:visible;" xmlns="http://www.w3.org/2000/svg">`;
  svg += `<defs>
    <linearGradient id="gTop" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#f0f4f8"/><stop offset="100%" stop-color="#dde4ed"/></linearGradient>
    <linearGradient id="gFront" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#f5ead0"/><stop offset="100%" stop-color="#e0cb99"/></linearGradient>
    <linearGradient id="gSide" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#b8c8da"/><stop offset="100%" stop-color="#8da3b8"/></linearGradient>
    <linearGradient id="gShelf" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#e8eef5"/><stop offset="100%" stop-color="#c8d4e0"/></linearGradient>
    <linearGradient id="gBack" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#e2e8f0"/><stop offset="100%" stop-color="#c8d0dc"/></linearGradient>
    <linearGradient id="gAcrylic" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#e0f2fe" stop-opacity="0.6"/><stop offset="50%" stop-color="#bae6fd" stop-opacity="0.4"/><stop offset="100%" stop-color="#7dd3fc" stop-opacity="0.5"/></linearGradient>
    <linearGradient id="gLedBar" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#fbbf24" stop-opacity="0.9"/><stop offset="50%" stop-color="#f59e0b"/><stop offset="100%" stop-color="#fbbf24" stop-opacity="0.9"/></linearGradient>
    <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#b91c1c" /></marker>
  </defs>`;

  let labelsHtml = '';

  for (let i = drawCount - 1; i >= 0; i--) {
    const ox = (wScale + unitGap) * 0.866 * (drawCount - 1) + 30 - i * (wScale + unitGap) * 0.866;
    const oy = hScale + 20 + i * (wScale + unitGap) * 0.5;
    const makePt = makeTransform(ox, oy);
    const ptA = makeTransformA(ox, oy);

    // 파츠 렌더링
    for (const p of positionedParts) {
      if (p.role === 'door' && spec.openPanels && spec.openPanels.some(op => op.side === 'front')) continue;
      
      svg += drawBox(
        p.posX! * scale, p.posY! * scale, p.posZ! * scale, 
        p.w * scale, p.d * scale, p.h * scale, 
        scale, makePt, p.role
      );
    }
    
    // OPEN 라벨
    if (spec.openPanels && spec.openPanels.length > 0 && i === 0) {
      const op = spec.openPanels.find(o => o.side === 'front');
      if (op && op.ratio > 0.05) {
        const [mlx, mly] = ptA(W/2 * scale, D/2 * scale, H/2 * scale);
        svg += `<rect x="${(mlx-18).toFixed(1)}" y="${(mly-8).toFixed(1)}" width="36" height="16" fill="#fef3c7" rx="3" stroke="#d97706" stroke-width="0.8" opacity="0.9"/>`;
        svg += `<text x="${mlx.toFixed(1)}" y="${mly.toFixed(1)}" font-size="7" font-weight="900" fill="#92400e" text-anchor="middle" dominant-baseline="middle">OPEN ${Math.round(op.ratio*100)}%</text>`;
      }
    }

    if (i === 0) {
      // 치수선
      const [wS, wSY] = ptA(0, 0, -6 * scale);
      const [wE, wEY] = ptA(wScale, 0, -6 * scale);
      const [wM, wMY] = ptA(wScale/2, 0, -6 * scale);
      labelsHtml += `<line x1="${wS.toFixed(1)}" y1="${wSY.toFixed(1)}" x2="${wE.toFixed(1)}" y2="${wEY.toFixed(1)}" stroke="#b91c1c" stroke-width="0.8" marker-start="url(#arrow)" marker-end="url(#arrow)"/>`;
      labelsHtml += `<text x="${wM.toFixed(1)}" y="${(wMY-4).toFixed(1)}" font-size="8" font-weight="900" fill="#b91c1c" text-anchor="middle">W${W}</text>`;

      const [dS, dSY] = ptA(wScale+2, 0, -4 * scale);
      const [dE, dEY] = ptA(wScale+2, dScale, -4 * scale);
      const [dM, dMY] = ptA(wScale+2, dScale/2, -4 * scale);
      labelsHtml += `<line x1="${dS.toFixed(1)}" y1="${dSY.toFixed(1)}" x2="${dE.toFixed(1)}" y2="${dEY.toFixed(1)}" stroke="#059669" stroke-width="0.8"/>`;
      labelsHtml += `<text x="${(dM+8).toFixed(1)}" y="${(dMY-2).toFixed(1)}" font-size="8" font-weight="900" fill="#059669" text-anchor="start">D${D}</text>`;

      const [hS, hSY] = ptA(-4, 0, 0);
      const [hE, hEY] = ptA(-4, 0, hScale);
      const [hM, hMY] = ptA(-4, 0, hScale/2);
      labelsHtml += `<line x1="${hS.toFixed(1)}" y1="${hSY.toFixed(1)}" x2="${hE.toFixed(1)}" y2="${hEY.toFixed(1)}" stroke="#0369a1" stroke-width="0.8"/>`;
      labelsHtml += `<text x="${(hM-12).toFixed(1)}" y="${hMY.toFixed(1)}" font-size="8" font-weight="900" fill="#0369a1" text-anchor="end" dominant-baseline="middle">H${H}</text>`;
    }
    
    if (i === 0 && bayCount > drawCount) {
       const [dtX, dtY] = ptA(wScale/2, dScale, -60);
       labelsHtml += `<text x="${dtX.toFixed(1)}" y="${dtY.toFixed(1)}" font-size="10" font-weight="900" fill="#1e293b" letter-spacing="1" text-anchor="middle">총 ${bayCount}세트 연속배치</text>`;
    }
  }

  svg += labelsHtml;
  svg += '</svg></div>';
  return svg;
}

export function renderIsometricDataUrl(spec: ModuleSpec): string {
  const svg = renderIsometricSvg(spec);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
